import { useMemo, useState } from "react";
import {
    Calendar,
    Clock,
    Sparkles,
    User,
    ArrowRight,
    Check,
} from "lucide-react";
import { SERVICES, CONTACT } from "../data/site";

// Official WhatsApp icon
const WhatsAppIcon = ({ className = "" }) => (
    <svg viewBox="0 0 32 32" fill="currentColor" className={className}>
        <path d="M19.11 17.205c-.372 0-1.088 1.39-1.518 1.39-.077 0-.156-.039-.238-.077-.492-.246-1.415-.643-2.756-1.83-.804-.71-1.34-1.598-1.499-1.902-.16-.301-.014-.469.126-.607.13-.13.291-.334.437-.501.145-.169.194-.29.29-.484.098-.194.05-.36-.024-.502-.077-.14-.652-1.585-.893-2.171-.24-.586-.484-.484-.652-.484l-.556-.014c-.194 0-.501.072-.766.362-.263.288-1.006.981-1.006 2.393 0 1.412 1.03 2.775 1.175 2.966.144.19 2.055 3.14 4.98 4.406.694.3 1.235.481 1.658.615.696.226 1.33.194 1.83.117.559-.083 1.717-.704 1.958-1.383.242-.68.242-1.262.17-1.383-.075-.121-.269-.194-.564-.336m-5.446 7.443h-.014a9.87 9.87 0 01-5.032-1.383l-.36-.214-3.744.984 1-3.65-.234-.376a9.833 9.833 0 01-1.51-5.256c.001-5.44 4.427-9.868 9.892-9.868a9.798 9.798 0 016.987 2.892 9.815 9.815 0 012.892 6.99c-.002 5.44-4.428 9.869-9.891 9.869M23.554 8.464a11.816 11.816 0 00-8.418-3.487c-6.554 0-11.89 5.335-11.892 11.893a11.86 11.86 0 001.588 5.945L3.104 28.867l6.208-1.629a11.883 11.883 0 005.685 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0023.554 8.464" />
    </svg>
);

// Time slots
const TIMES = [
    "10:00",
    "11:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "21:00",
    "22:00",
    "23:00",
];

// Turkish month names
const MONTHS = [
    "Ocak",
    "Şubat",
    "Mart",
    "Nisan",
    "Mayıs",
    "Haziran",
    "Temmuz",
    "Ağustos",
    "Eylül",
    "Ekim",
    "Kasım",
    "Aralık",
];

function formatTRDate(iso) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export default function BookingForm() {
    const services = SERVICES.filter((s) => !s.hiddenFromGrid);

    const today = useMemo(() => {
        const d = new Date();
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    }, []);

    const [service, setService] = useState(services[0].title);
    const [date, setDate] = useState(today);
    const [time, setTime] = useState("14:00");
    const [name, setName] = useState("");
    const [sent, setSent] = useState(false);

    const buildMessage = () => {
        const lines = [
            "Merhaba, Cegu Masaj Salonu için randevu almak istiyorum.",
            "",
            `• Hizmet: ${service}`,
            `• Tarih: ${formatTRDate(date)}`,
            `• Saat: ${time}`,
            name ? `• İsim: ${name}` : null,
            "",
            "Uygun musunuz? Ayrıca %15 hoşgeldin indirimi (CEGU15) uygulanabilir mi?",
        ]
            .filter(Boolean)
            .join("\n");
        return encodeURIComponent(lines);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const url = `https://wa.me/${CONTACT.whatsappRaw}?text=${buildMessage()}`;
        window.open(url, "_blank", "noopener");
        setSent(true);
        setTimeout(() => setSent(false), 3500);
    };

    return (
        <section
            id="randevu"
            data-testid="booking-section"
            className="section bg-[#0a0a0c] relative overflow-hidden"
        >
            {/* subtle gold glow */}
            <div className="absolute top-1/3 -right-40 w-[480px] h-[480px] rounded-full bg-[#c9a24b]/10 blur-[130px] pointer-events-none" />
            <div className="absolute bottom-0 -left-40 w-[420px] h-[420px] rounded-full bg-[#f5d685]/6 blur-[130px] pointer-events-none" />

            <div className="container-x relative grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                {/* LEFT — text */}
                <div className="lg:col-span-5">
                    <div className="eyebrow mb-4">Hızlı Randevu</div>
                    <h2 className="h2 text-[#f4efe4]">
                        <span className="gold-text">3 Adımda</span> Randevunuzu
                        Oluşturun
                    </h2>
                    <div className="divider-gold mt-6" />
                    <p className="mt-6 body-base max-w-md">
                        Aşağıdaki formu doldurun, WhatsApp üzerinden birkaç
                        saniyede size geri dönelim. Onaylanan randevunuzda %15
                        hoşgeldin indirimi otomatik uygulanır.
                    </p>

                    <ul className="mt-8 space-y-3">
                        {[
                            "Hizmet + tarih + saat seçin",
                            "WhatsApp'ta anında iletim",
                            "%15 hoşgeldin indirimi",
                        ].map((t, i) => (
                            <li
                                key={t}
                                className="flex items-center gap-3 text-[14px] text-[#c9c2b3]"
                            >
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#f5d685] to-[#8a6a28] text-[#0a0a0c] flex items-center justify-center text-[12px] font-bold shrink-0">
                                    {i + 1}
                                </div>
                                {t}
                            </li>
                        ))}
                    </ul>

                    {/* Quick contact */}
                    <div className="mt-8 pt-6 border-t border-[#23232b]">
                        <div className="text-[11px] uppercase tracking-[0.24em] text-[#7d766a] mb-2">
                            Ya da doğrudan
                        </div>
                        <div className="flex flex-wrap gap-4 text-[14px]">
                            <a
                                href={`tel:${CONTACT.phoneRaw}`}
                                className="text-[#f4efe4] hover:text-[#f5d685]"
                            >
                                {CONTACT.phone}
                            </a>
                            <span className="text-[#7d766a]">·</span>
                            <a
                                href={`https://wa.me/${CONTACT.whatsappRaw}`}
                                target="_blank"
                                rel="noreferrer"
                                className="text-[#f4efe4] hover:text-[#f5d685]"
                            >
                                {CONTACT.whatsapp}
                            </a>
                        </div>
                    </div>
                </div>

                {/* RIGHT — form card */}
                <div className="lg:col-span-7">
                    <div className="relative">
                        {/* gold outer glow */}
                        <div className="absolute -inset-2 rounded-[28px] bg-gradient-to-br from-[#c9a24b]/25 via-transparent to-[#c9a24b]/15 blur-lg -z-10" />

                        <form
                            onSubmit={onSubmit}
                            data-testid="booking-form"
                            className="relative rounded-2xl border border-[#c9a24b]/25 bg-gradient-to-br from-[#14141a] via-[#0f0f11] to-[#14141a] p-6 md:p-8 lg:p-10"
                        >
                            {/* corner ornaments */}
                            <span className="absolute top-0 left-0 w-10 h-px bg-[#c9a24b]" />
                            <span className="absolute top-0 left-0 w-px h-10 bg-[#c9a24b]" />
                            <span className="absolute bottom-0 right-0 w-10 h-px bg-[#c9a24b]" />
                            <span className="absolute bottom-0 right-0 w-px h-10 bg-[#c9a24b]" />

                            <div className="flex items-center gap-3 mb-6">
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#f5d685] to-[#8a6a28] flex items-center justify-center">
                                    <Sparkles className="w-4 h-4 text-[#0a0a0c]" />
                                </div>
                                <div>
                                    <div className="text-[10.5px] uppercase tracking-[0.24em] gold-text">
                                        Cegu Randevu Formu
                                    </div>
                                    <div className="text-[15px] font-semibold text-[#f4efe4]">
                                        Bilgilerinizi iletin
                                    </div>
                                </div>
                            </div>

                            <div className="grid sm:grid-cols-2 gap-4">
                                {/* Service */}
                                <div className="sm:col-span-2">
                                    <label className="text-[11px] uppercase tracking-[0.22em] text-[#c9a24b] flex items-center gap-2 mb-2">
                                        <Sparkles className="w-3 h-3" />
                                        Hizmet
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={service}
                                            onChange={(e) =>
                                                setService(e.target.value)
                                            }
                                            data-testid="booking-service"
                                            required
                                            className="w-full appearance-none bg-[#17171b] border border-[#26262c] hover:border-[#c9a24b]/50 focus:border-[#c9a24b] focus:outline-none rounded-lg px-4 py-3.5 text-[14px] text-[#f4efe4] transition pr-10 cursor-pointer"
                                        >
                                            {services.map((s) => (
                                                <option
                                                    key={s.slug}
                                                    value={s.title}
                                                >
                                                    {s.title} · {s.duration}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#c9a24b]">
                                            <ArrowRight className="w-4 h-4 rotate-90" />
                                        </div>
                                    </div>
                                </div>

                                {/* Date */}
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.22em] text-[#c9a24b] flex items-center gap-2 mb-2">
                                        <Calendar className="w-3 h-3" />
                                        Tarih
                                    </label>
                                    <input
                                        type="date"
                                        value={date}
                                        min={today}
                                        onChange={(e) => setDate(e.target.value)}
                                        data-testid="booking-date"
                                        required
                                        className="w-full bg-[#17171b] border border-[#26262c] hover:border-[#c9a24b]/50 focus:border-[#c9a24b] focus:outline-none rounded-lg px-4 py-3.5 text-[14px] text-[#f4efe4] transition [color-scheme:dark]"
                                    />
                                </div>

                                {/* Time */}
                                <div>
                                    <label className="text-[11px] uppercase tracking-[0.22em] text-[#c9a24b] flex items-center gap-2 mb-2">
                                        <Clock className="w-3 h-3" />
                                        Saat
                                    </label>
                                    <div className="relative">
                                        <select
                                            value={time}
                                            onChange={(e) =>
                                                setTime(e.target.value)
                                            }
                                            data-testid="booking-time"
                                            required
                                            className="w-full appearance-none bg-[#17171b] border border-[#26262c] hover:border-[#c9a24b]/50 focus:border-[#c9a24b] focus:outline-none rounded-lg px-4 py-3.5 text-[14px] text-[#f4efe4] transition pr-10 cursor-pointer"
                                        >
                                            {TIMES.map((t) => (
                                                <option key={t} value={t}>
                                                    {t}
                                                </option>
                                            ))}
                                        </select>
                                        <div className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#c9a24b]">
                                            <Clock className="w-4 h-4" />
                                        </div>
                                    </div>
                                </div>

                                {/* Name (optional) */}
                                <div className="sm:col-span-2">
                                    <label className="text-[11px] uppercase tracking-[0.22em] text-[#c9a24b] flex items-center gap-2 mb-2">
                                        <User className="w-3 h-3" />
                                        İsim (opsiyonel)
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={(e) => setName(e.target.value)}
                                        data-testid="booking-name"
                                        placeholder="Adınız"
                                        maxLength={40}
                                        className="w-full bg-[#17171b] border border-[#26262c] hover:border-[#c9a24b]/50 focus:border-[#c9a24b] focus:outline-none rounded-lg px-4 py-3.5 text-[14px] text-[#f4efe4] placeholder:text-[#7d766a] transition"
                                    />
                                </div>
                            </div>

                            {/* Summary preview */}
                            <div className="mt-6 p-4 rounded-lg bg-[#0a0a0c] border border-[#23232b] text-[13px] text-[#b8b1a1] leading-relaxed">
                                <div className="text-[10.5px] uppercase tracking-[0.24em] text-[#7d766a] mb-2">
                                    Özet
                                </div>
                                <span className="text-[#f4efe4]">
                                    {service}
                                </span>
                                {" · "}
                                <span className="text-[#f4efe4]">
                                    {formatTRDate(date)}
                                </span>
                                {" · "}
                                <span className="text-[#f4efe4]">{time}</span>
                                <span className="ml-2 inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border border-[#c9a24b]/30 text-[#f5d685]">
                                    <Sparkles className="w-3 h-3" />
                                    %15 indirim eklenir
                                </span>
                            </div>

                            <button
                                type="submit"
                                data-testid="booking-submit"
                                className="btn w-full mt-5 !py-4 bg-[#25D366] hover:bg-[#1fbe5a] text-white border border-[#25D366] !text-[14px] font-semibold shadow-lg shadow-[#25D366]/20"
                            >
                                {sent ? (
                                    <>
                                        <Check className="w-5 h-5" />
                                        WhatsApp'a yönlendirildi
                                    </>
                                ) : (
                                    <>
                                        <WhatsAppIcon className="w-5 h-5" />
                                        WhatsApp'ta Randevuyu Gönder
                                    </>
                                )}
                            </button>

                            <p className="mt-3 text-center text-[11px] text-[#7d766a]">
                                Bilgileriniz WhatsApp üzerinden salonumuza
                                iletilir. Sadece randevu yönetimi için kullanılır.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
}
